import { Modal } from 'antd';
import { useState } from 'react';

/** A password the server will never show again, and who it belongs to. */
export interface IssuedPassword {
  /** Named by what they sign in with, not by where it might be emailed. */
  username: string;
  password: string;
}

/**
 * The one-time password, shown once.
 *
 * <p>
 * <strong>Modal rather than a toast, and it does not close on a click outside</strong>: this value
 * exists nowhere else — not in the database in retrievable form, not on a later screen — and the
 * administrator has to pass it on before dismissing it. A message that fades after four seconds
 * would mean resetting the password again, which ends the user's sessions a second time.
 * </p>
 *
 * <p>
 * <strong>Shared rather than private to the Users screen, because there are now two ways to mint
 * an account.</strong> Giving a member of staff a login issues exactly the same value under exactly
 * the same terms, and a second copy of this modal would be a second place for the wording and the
 * clipboard fallback to drift — on the half nobody looks at.
 * </p>
 */
export function OneTimePasswordModal({
  issued,
  onClose,
}: {
  issued: IssuedPassword | null;
  onClose: () => void;
}) {
  const [copied, setCopied] = useState(false);

  return (
    <Modal
      open={issued !== null}
      onCancel={onClose}
      onOk={onClose}
      maskClosable={false}
      okText="I have passed it on"
      cancelButtonProps={{ style: { display: 'none' } }}
      title="One-time password"
      afterClose={() => setCopied(false)}
    >
      <div className="space-y-3.5 py-2">
        <p className="text-[length:var(--scale-font-size)] text-gray-900">
          Give this to <strong>{issued?.username}</strong>. It is shown once and cannot be retrieved
          again — they will be asked to change it as they sign in.
        </p>

        <div className="flex items-center gap-2 rounded-md border border-border-color bg-light px-3 py-2.5">
          <code className="font-mono-nums flex-1 text-[15px] tracking-wider text-gray-900">
            {issued?.password}
          </code>

          <button
            type="button"
            className="inline-flex size-7 shrink-0 cursor-pointer items-center justify-center rounded-md border border-border-color bg-white hover:bg-light"
            aria-label="Copy password"
            onClick={() => {
              void navigator.clipboard
                .writeText(issued?.password ?? '')
                .then(() => setCopied(true))
                // Clipboard access can be refused outright (insecure context, or a permission
                // policy). Saying nothing would look like the copy worked, and this is the one
                // value on the screen that cannot be fetched again.
                .catch(() => setCopied(false));
            }}
          >
            <i className={`icon ${copied ? 'icon-check text-success' : 'icon-copy'} text-[13px]`} />
          </button>
        </div>
      </div>
    </Modal>
  );
}
